import { api } from './api'
import { getAll } from './pagination'

export interface StudentPoints {
  id: number
  student: number
  student_name: string
  student_class: string
  total_points: number
  level: number
  experience: number
  streak_days: number
  last_activity: string
  academic_points: number
  attendance_points: number
  behavior_points: number
  participation_points: number
  sports_points: number
  level_progress: number
}

export interface Badge {
  id: number
  name: string
  description: string
  badge_type: string
  icon: string
  points_required: number
  is_active: boolean
  created_at: string
}

export interface StudentBadge {
  id: number
  student: number
  student_name: string
  badge: Badge
  earned_date: string
  is_displayed: boolean
  created_at: string
}

export interface Challenge {
  id: number
  title: string
  description: string
  points_reward: number
  difficulty_level: number
  start_date: string
  end_date: string
  subject: number
  subject_name: string
  class_assigned: number
  class_name: string
  academic_year: number
  created_by: number
  created_by_name: string
  is_active: boolean
  created_at: string
}

export interface ChallengeSubmission {
  id: number
  student: number
  student_name: string
  challenge: number
  challenge_title: string
  submission_text: string
  submission_file?: string
  submitted_at: string
  is_approved: boolean
  points_awarded: number
  feedback?: string
  reviewed_by?: number
  reviewed_at?: string
}

export interface LeaderboardEntry {
  id: number
  student: number
  student_name: string
  student_class: string
  points: number
  rank: number
  period: string
  created_at: string
}

export interface PointsTransaction {
  id: number
  student: number
  student_name: string
  points: number
  transaction_type: string
  description: string
  reference_id?: number
  reference_type?: string
  created_at: string
}

export interface Achievement {
  id: number
  student: number
  student_name: string
  title: string
  description: string
  achievement_type: string
  points_awarded: number
  awarded_date: string
  created_at: string
}

export interface Streak {
  id: number
  student: number
  student_name: string
  streak_type: string
  current_streak: number
  longest_streak: number
  last_activity_date?: string
  created_at: string
}

export interface GamificationDashboard {
  student_points: StudentPoints
  recent_badges: StudentBadge[]
  active_challenges: Challenge[]
  leaderboard_position: number
  recent_transactions: PointsTransaction[]
  achievements: Achievement[]
  streaks: Streak[]
}

export interface GamificationStats {
  overview: {
    total_students: number
    students_with_points: number
    total_points_awarded: number
    total_badges: number
    badges_awarded: number
    active_challenges: number
  }
  top_students: StudentPoints[]
}

export interface BulkPointsAward {
  student_ids: number[]
  points: number
  activity_type: string
  description: string
  category: 'academic' | 'attendance' | 'behavior' | 'participation' | 'sports'
}

class GamificationService {
  // Student Points
  async getStudentPoints(studentId?: number): Promise<StudentPoints> {
    const url = studentId 
      ? `students/gamification/points/${studentId}/`
      : 'students/gamification/points/'
    const response = await api.get(url)
    return response.data
  }

  // Badges
  async getBadges(): Promise<Badge[]> {
    return getAll('students/gamification/badges/')
  }

  async getStudentBadges(studentId?: number): Promise<StudentBadge[]> {
    const url = studentId 
      ? `students/gamification/badges/student/${studentId}/`
      : 'students/gamification/badges/student/'
    return getAll(url)
  }

  // Challenges
  async getChallenges(): Promise<Challenge[]> {
    return getAll('students/gamification/challenges/')
  }

  async getChallengeSubmissions(studentId?: number): Promise<ChallengeSubmission[]> {
    const url = studentId 
      ? `students/gamification/challenges/submissions/?student=${studentId}`
      : 'students/gamification/challenges/submissions/'
    return getAll(url)
  }

  async submitChallenge(data: {
    challenge: number
    submission_text: string
    submission_file?: File
  }): Promise<ChallengeSubmission> {
    const formData = new FormData()
    formData.append('challenge', data.challenge.toString())
    formData.append('submission_text', data.submission_text)
    if (data.submission_file) {
      formData.append('submission_file', data.submission_file)
    }

    const response = await api.post('students/gamification/challenges/submissions/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  }

  // Leaderboard
  async getLeaderboard(period: 'week' | 'month' | 'year' = 'month'): Promise<LeaderboardEntry[]> {
    const url = `students/gamification/leaderboard/?period=${period}`
    return getAll(url)
  }

  // Points Transactions
  async getPointsTransactions(studentId?: number): Promise<PointsTransaction[]> {
    const url = studentId 
      ? `students/gamification/transactions/?student=${studentId}`
      : 'students/gamification/transactions/'
    return getAll(url)
  }

  // Achievements
  async getAchievements(studentId?: number): Promise<Achievement[]> {
    const url = studentId 
      ? `students/gamification/achievements/?student=${studentId}`
      : 'students/gamification/achievements/'
    return getAll(url)
  }

  // Streaks
  async getStreaks(studentId?: number): Promise<Streak[]> {
    const url = studentId 
      ? `students/gamification/streaks/?student=${studentId}`
      : 'students/gamification/streaks/'
    return getAll(url)
  }

  // Dashboard
  async getDashboard(studentId?: number): Promise<GamificationDashboard> {
    const url = studentId 
      ? `students/gamification/dashboard/?student_id=${studentId}`
      : 'students/gamification/dashboard/'
    const response = await api.get(url)
    return response.data
  }

  // Points Management (Teacher/Admin only)
  async awardPoints(data: {
    student_id: number
    activity_type: string
    points?: number
    description: string
    reference_id?: number
    reference_type?: string
  }): Promise<{ message: string; points_record: StudentPoints }> {
    const response = await api.post('students/gamification/award-points/', data)
    return response.data
  }

  async bulkAwardPoints(data: BulkPointsAward): Promise<{
    message: string
    awarded_students: Array<{
      student_id: number
      student_name: string
      total_points: number
    }>
  }> {
    const response = await api.post('students/gamification/bulk-award-points/', data)
    return response.data
  }

  // Statistics
  async getStats(): Promise<GamificationStats> {
    const response = await api.get('students/gamification/stats/')
    return response.data
  }
}

export const gamificationService = new GamificationService()
